import { EventEmitter, Injectable, Output, TemplateRef } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';

type TablePages = 'scenes' | 'painter';

@Injectable()
export class ContainerService {

    zoom = new BehaviorSubject(null);
    resetMoveable = new BehaviorSubject(null);
    editorMode = new BehaviorSubject(null);
    swappingIds = new BehaviorSubject(null);
    scheduleEventSelectedIndex = new BehaviorSubject(null);
    scheduleSelectedEvent = new BehaviorSubject(null);
    activeSceneItemIds = new BehaviorSubject(null);
    // one-shot request to select item(s) on the canvas once the TARGET scene's moveables exist. Set by
    // breadcrumb item-navigation when it changes scene (the canvas isn't rendered yet, so a direct
    // activeSceneItemIds push can't land). scene-builder consumes + clears it on the next sceneLoaded.
    requestCanvasSelect = new BehaviorSubject<{ sceneId: any, ids: any[] } | null>(null);
    toggleGuideline = new BehaviorSubject(null);
    isMouseOnScene = new BehaviorSubject(null);
    isMouseOnPlaylist = new BehaviorSubject(null);
    toolbarVisible = new BehaviorSubject<boolean>(true);
    // The editor toolbar's TemplateRef, published by scene-builder (which owns the toolbar markup + its
    // action handlers) and consumed by scene-editor, which renders it as a full-width bar under the
    // breadcrumb. Null whenever no scene-builder is mounted (e.g. playlist/collection/location modes).
    toolbarTemplate = new BehaviorSubject<TemplateRef<any> | null>(null);
    // The preview + register-player actions, declared in scene-builder (so they keep their bindings/handlers)
    // but rendered by scene-editor pinned to the RIGHT of the breadcrumb bar. Null whenever no scene-builder
    // is mounted, same lifecycle as toolbarTemplate.
    breadcrumbActionsTemplate = new BehaviorSubject<TemplateRef<any> | null>(null);
    // The Design | Playlists view switcher, declared in scene-builder (keeps its bindings) but rendered by
    // scene-editor next to the scene name on the breadcrumb bar. Null whenever no scene-builder is mounted.
    viewSwitcherTemplate = new BehaviorSubject<TemplateRef<any> | null>(null);
    // The toolbar markup is DECLARED in scene-builder but INSTANTIATED as an embedded view in scene-editor
    // (ngTemplateOutlet). Its bindings read scene-builder's fields (experienceLevel, disableUndo, ...) but the
    // embedded view is change-detected as part of scene-editor's tree, NOT scene-builder's. So scene-builder's
    // own `cd.markForCheck()` can't refresh it — a value like `experienceLevel` (loaded async from localStorage)
    // updates the field but the toolbar keeps rendering the stale value until an unrelated global CD tick
    // (e.g. a window resize) happens to re-check scene-editor. scene-builder pings this Subject whenever a
    // toolbar-relevant field changes; scene-editor subscribes and runs detectChanges() to re-render the bar.
    toolbarRefresh = new Subject<void>();
    // True when the editor toolbar has spilled onto a 2nd+ line at the current width. Measured by scene-editor
    // (which owns the toolbar ResizeObserver) from the group boxes' row positions and consumed by scene-builder
    // to show/hide the collapse/expand chevron (the chevron only appears when there's a 2nd line to collapse).
    toolbarWrapped = new BehaviorSubject<boolean>(false);
    downloadedSharedScenes = new BehaviorSubject(null);
    // focused single-component edit (a component embedded in a playlist page): which component, in which
    // scene, and the parent playlist's region size (the canvas is sized to that region - the slot the
    // component renders in at runtime). When set, the canvas renders ONLY that component and the toolbox hides
    // - UNLESS needsToolbox is true (an aggregator/data-provider that must be populated from the toolbox).
    componentEditTarget = new BehaviorSubject<{ sceneId: any, componentId: any, regionWidth?: any, regionHeight?: any, needsToolbox?: boolean, fitComponent?: boolean, groupEdit?: boolean } | null>(null);
    // THE container the canvas is currently editing, i.e. whose children the canvas shows and every structural
    // op (add / paste / duplicate / delete / z-order) must write into: the scene id normally, the GROUP id while
    // drilled into a group, and null in a single-component focused edit (nothing to restructure -> refuse).
    // Published by scene-builder in the SAME pass that captures its `players` from the effective scene data, so
    // "what the canvas shows" and "where a write lands" can never drift apart - a drift would dump a group's
    // children into the scene root.
    sceneContainerId = new BehaviorSubject<any>(null);
    // Current drill depth in the visible breadcrumb trail: 0 = root scene, 1 = first drilled level, etc.
    // Published by scene-editor (which owns displayBreadcrumbs, the single trail that unifies nested-scene AND
    // component/group drilling). scene-builder uses it to route the editor zoom: depth 0 = the persisted root
    // zoom, depth N = the runtime zoom stack entry for that level.
    drillDepth = new BehaviorSubject<number>(0);
    // Runtime zoom stack + last-applied depth. MUST live here (not on scene-builder): drilling into a nested
    // scene destroys and recreates scene-builder (renderSceneContainer *ngIf toggle), which would reset any
    // component-held stack. zoomStack[d-1] is the zoom for drill depth d; zoomDrillDepth is the last-applied
    // (effective) depth. Depth 0 (root) is NOT kept here — it is the persisted localStorage 'zoom'.
    zoomStack: number[] = [];
    zoomDrillDepth = 0;

    tableSelection = {
        scenes: {},
        painter: {}
    };
    stickerColor = '';
    blockSmallView = false;

    private $markedTableRow = new BehaviorSubject<any>(null);
    private playlistData = null;
    private categoryList = new BehaviorSubject(null);
    private crudScenePlayer = new BehaviorSubject(null);
    private $dndItem = new BehaviorSubject<any>(null);

    get playlist() {
        return this.playlistData;
    }

    set playlist(i_playlistData) {
        this.playlistData = i_playlistData;
    }

    constructor() {
    }

    updateZoom(ref) {
        this.zoom.next(ref);
    }

    updateResetMoveable(status) {
        this.resetMoveable.next(status);
    }

    updateEditorMode(mode: any) {
        this.editorMode.next(mode);
    }

    updateComponentEditTarget(target: { sceneId: any, componentId: any, regionWidth?: any, regionHeight?: any, needsToolbox?: boolean, fitComponent?: boolean, groupEdit?: boolean } | null) {
        this.componentEditTarget.next(target);
    }

    updateDrillDepth(depth: number) {
        if (this.drillDepth.value !== depth) this.drillDepth.next(depth);
    }

    setToolbarVisible(visible: boolean) {
        this.toolbarVisible.next(visible);
    }

    setToolbarTemplate(tpl: TemplateRef<any> | null) {
        this.toolbarTemplate.next(tpl);
    }

    setBreadcrumbActionsTemplate(tpl: TemplateRef<any> | null) {
        this.breadcrumbActionsTemplate.next(tpl);
    }

    setViewSwitcherTemplate(tpl: TemplateRef<any> | null) {
        this.viewSwitcherTemplate.next(tpl);
    }

    // Ask scene-editor to re-check the relocated editor toolbar (see `toolbarRefresh` above).
    refreshToolbar() {
        this.toolbarRefresh.next();
    }

    getCategories() {
        return this.categoryList;
    }

    setCategories(categories: string[]) {
        this.categoryList.next(categories);
    }

    setScheduleEventSelectedIndex(data) {
        this.scheduleEventSelectedIndex.next(data);
    }

    setScheduleSelectedEvent(data) {
        this.scheduleSelectedEvent.next(data);
    }

    setTableSelection(page: TablePages, value) {
        if (!page || !value) return;

        Object.keys(value).forEach(key => {
            this.tableSelection[page][key] = value[key];
        });
    }

    getTableSelection(page: TablePages) {
        if (!page) return null;
        return this.tableSelection[page];
    }

    setMarkedTableRow(sceneId) {
        this.$markedTableRow.next(sceneId);
    }

    getMarkedTableRow() {
        return this.$markedTableRow;
    }

    listenCrudScenePlayer() {
        return this.crudScenePlayer;
    }

    setCrudScenePlayer(crudData) {
        this.crudScenePlayer.next(crudData);
    }

    getActiveSceneItemIds() {
        return this.activeSceneItemIds;
    }

    listenDndContent() {
        return this.$dndItem;
    }

    setDndContent(item, component = null, isDrop = false, isDragging = false, dropPointX = 0, dropPointY = 0) {
        if (item) {
            this.$dndItem.next({
                item,
                component,
                isDrop,
                isDragging,
                dropPointX,
                dropPointY
            });
        } else {
            this.$dndItem.next(null);
        }
    }
}
